import { useEffect, useState } from "react";
import axios from "axios";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { scaleLinear } from "d3-scale";
import { STATE_NAME_TO_ABBR } from "@/lib/usStateNames";
import { openPreRegister } from "@/lib/preRegisterModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const GEO_URL = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";
const LAUNCHED_STATES = ["FL", "CA", "TX"];
const LAUNCHED_COLOR = "#F2761E";

export const StateHeatmap = () => {
  const [counts, setCounts] = useState({});
  const [hovered, setHovered] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/leads/states/heatmap`)
      .then((res) => {
        const map = {};
        (res.data?.states || []).forEach((s) => { map[s.state] = s.count; });
        setCounts(map);
      })
      .catch(() => setCounts({}));
  }, []);

  const maxCount = Math.max(1, ...Object.values(counts));
  const colorScale = scaleLinear().domain([0, maxCount]).range(["#E9EEF3", "#F2761E"]);
  const statesInterested = Object.values(counts).filter((c) => c > 0).length;

  return (
    <div data-testid="state-heatmap" className="relative w-full">
      {statesInterested > 0 && (
        <div data-testid="heatmap-states-interested-count" className="text-center mb-4">
          <span className="inline-flex items-center gap-1.5 text-sm font-bold text-rr-navy bg-rr-orange/10 rounded-full px-4 py-1.5">
            {statesInterested} state{statesInterested === 1 ? "" : "s"} already interested
          </span>
        </div>
      )}
      <ComposableMap projection="geoAlbersUsa" width={800} height={480} style={{ width: "100%", height: "auto" }}>
        <Geographies geography={GEO_URL}>
          {({ geographies }) =>
            geographies.map((geo) => {
              const abbr = STATE_NAME_TO_ABBR[geo.properties.name];
              if (!abbr) return null;
              const count = counts[abbr] || 0;
              const launched = LAUNCHED_STATES.includes(abbr);
              return (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  data-testid={`heatmap-state-${abbr}`}
                  onMouseEnter={() => setHovered({ name: geo.properties.name, count, launched })}
                  onMouseLeave={() => setHovered(null)}
                  onClick={() => openPreRegister("driver", abbr)}
                  fill={launched ? LAUNCHED_COLOR : count > 0 ? colorScale(count) : "#E9EEF3"}
                  stroke="#FFFFFF"
                  strokeWidth={0.75}
                  style={{ outline: "none", cursor: "pointer" }}
                />
              );
            })
          }
        </Geographies>
      </ComposableMap>

      {hovered && (
        <div
          data-testid="heatmap-tooltip"
          className="absolute top-2 left-1/2 -translate-x-1/2 bg-rr-navy text-white text-xs font-bold px-4 py-2 rounded-full shadow-lg whitespace-nowrap"
        >
          {hovered.launched
            ? `${hovered.name}: Now Launching · Click to pre-register`
            : `${hovered.name}: ${hovered.count} sign-up${hovered.count === 1 ? "" : "s"} · Click to pre-register`}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mt-3 text-xs text-rr-muted">
        <span className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full" style={{ background: LAUNCHED_COLOR }} /> Launching Here
        </span>
        <span className="flex items-center gap-2">
          <span>Fewer sign-ups</span>
          <span className="w-24 h-2 rounded-full" style={{ background: "linear-gradient(90deg, #E9EEF3, #F2761E)" }} />
          <span>More sign-ups</span>
        </span>
      </div>
      <p className="text-center text-xs text-rr-muted mt-2">Click your state to pre-register.</p>
    </div>
  );
};
